import { TFunction } from "i18next";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import { AccessModuleService } from "../../Services/AccessModuleService";
import { CustomerService } from "../../Services/customerService";
import { IArticleCategoryLink, IKnowledgeArticleModel } from "../../Services/KnowledgeArticleService";
import { IKnowledgeArticleSectionModel, SECTION_TYPE } from "../../Services/KnowledgeArticleSectionService";
import {
    IMediaAssetModel,
    MEDIA_TYPE,
    MediaAssetService,
    isImageAttachment,
    isVideoAttachment,
} from "../../Services/MediaAssetService";
import { formatDisplayDate } from "../../Util/dateUtil";
import {
    PDF_CONTENT_WIDTH,
    PDF_EMPTY_VALUE,
    PDF_FONT_BODY,
    PDF_FONT_LABEL,
    PDF_FONT_SMALL,
    PDF_FONT_TITLE,
    PDF_LINE_HEIGHT,
    PDF_MARGIN_X,
    PDF_MARGIN_TOP,
    PDF_PAGE_WIDTH,
    PDF_SECTION_GAP,
    drawDashboardTitle,
    ensurePageSpace,
    buildFinalizedPdfBlob,
    setTextColor,
    writeFieldGrid,
    writeWrappedLines,
} from "../Project/projectPdfExportShared";
import { resolveCustomerPdfLogo, resolveDashlyPdfLogo } from "../Project/projectPdfLogo";
import { resolveProjectPdfTheme } from "../Project/projectPdfTheme";
import { estimateReadingTimeMinutes, isFeaturedActive, isFeaturedExpired } from "./knowledgeBaseConstants";

const PDF_IMAGE_MAX_HEIGHT_MM = 90;
const PDF_IMAGE_MAX_WIDTH_PX = 1200;
const PDF_IMAGE_JPEG_QUALITY = 0.82;
const PDF_THUMB_HEIGHT_MM = 18;

interface IPdfEmbedImage {
    dataUrl: string;
    format: "PNG" | "JPEG";
    widthPx: number;
    heightPx: number;
}

type HtmlBlock =
    | { kind: "heading"; text: string }
    | { kind: "paragraph"; text: string }
    | { kind: "list-item"; text: string }
    | { kind: "image"; assetUuid?: string }
    | { kind: "table"; rows: string[][] };

interface IExportKnowledgeArticleToPdfParams {
    article: IKnowledgeArticleModel;
    categories: IArticleCategoryLink[];
    sections: IKnowledgeArticleSectionModel[];
    mediaAssets: IMediaAssetModel[];
    mediaAssetService: MediaAssetService;
    t: TFunction;
}

const yesNo = (value: boolean, t: TFunction): string => (value ? t("common.yes") : t("common.no"));

const formatPdfDate = (value?: string | Date): string => {
    if (!value) {
        return PDF_EMPTY_VALUE;
    }
    if (value instanceof Date) {
        return formatDisplayDate(value.toISOString());
    }
    return formatDisplayDate(value);
};

const formatFileSize = (sizeBytes?: number): string => {
    if (!sizeBytes || sizeBytes <= 0) {
        return PDF_EMPTY_VALUE;
    }
    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }
    if (sizeBytes < 1024 * 1024) {
        return `${(sizeBytes / 1024).toFixed(1)} KB`;
    }
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const sectionTypeLabel = (sectionType: string, t: TFunction): string => {
    switch (sectionType) {
        case SECTION_TYPE.IMAGE:
            return t("knowledgeBase.form.sectionTypeImage");
        case SECTION_TYPE.VIDEO:
            return t("knowledgeBase.form.sectionTypeVideo");
        case SECTION_TYPE.SLIDESHOW:
            return t("knowledgeBase.form.sectionTypeSlideshow");
        case SECTION_TYPE.TEXT:
            return t("knowledgeBase.form.sectionTypeText");
        default:
            return sectionType;
    }
};

const attachmentTypeLabel = (asset: IMediaAssetModel, t: TFunction): string => {
    if (asset.mediaType === MEDIA_TYPE.PHOTO || isImageAttachment(asset)) {
        return t("knowledgeBase.form.sectionTypeImage");
    }
    if (asset.mediaType === MEDIA_TYPE.VIDEO || isVideoAttachment(asset)) {
        return t("knowledgeBase.form.sectionTypeVideo");
    }
    return t("knowledgeBase.export.fileType");
};

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Failed to load image"));
        image.src = dataUrl;
    });

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error ?? new Error("Failed to read image blob"));
        reader.readAsDataURL(blob);
    });

const normalizeImageForPdf = async (blob: Blob): Promise<IPdfEmbedImage> => {
    const dataUrl = await blobToDataUrl(blob);
    const image = await loadImage(dataUrl);
    const naturalWidth = Math.max(1, image.naturalWidth || 1);
    const naturalHeight = Math.max(1, image.naturalHeight || 1);
    const scale = Math.min(1, PDF_IMAGE_MAX_WIDTH_PX / naturalWidth);
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
        throw new Error("Failed to prepare image");
    }
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    return {
        dataUrl: canvas.toDataURL("image/jpeg", PDF_IMAGE_JPEG_QUALITY),
        format: "JPEG",
        widthPx: width,
        heightPx: height,
    };
};

const fitImageMm = (image: IPdfEmbedImage, maxWidthMm: number, maxHeightMm: number): { widthMm: number; heightMm: number } => {
    const aspect = image.widthPx / image.heightPx;
    let widthMm = maxWidthMm;
    let heightMm = widthMm / aspect;
    if (heightMm > maxHeightMm) {
        heightMm = maxHeightMm;
        widthMm = heightMm * aspect;
    }
    return { widthMm, heightMm };
};

const flushText = (blocks: HtmlBlock[], kind: "heading" | "paragraph" | "list-item", text: string) => {
    const trimmed = text.replace(/\u00a0/g, " ").replace(/[ \t]+\n/g, "\n").replace(/\s+/g, " ").trim();
    if (!trimmed) {
        return;
    }
    blocks.push({ kind, text: trimmed });
};

const parseTableRows = (table: HTMLTableElement): string[][] =>
    Array.from(table.rows).map((row) =>
        Array.from(row.cells).map((cell) => (cell.textContent ?? "").replace(/\s+/g, " ").trim())
    );

const parseHtmlBlocks = (html: string): HtmlBlock[] => {
    const blocks: HtmlBlock[] = [];
    const parsed = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = parsed.body.firstElementChild;
    if (!root) {
        return blocks;
    }

    const walk = (node: Node, mode: "flow" | "heading" | "list-item", buffer: { text: string }) => {
        if (node.nodeType === Node.TEXT_NODE) {
            buffer.text += node.textContent ?? "";
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        const element = node as HTMLElement;
        const tag = element.tagName.toLowerCase();
        if (tag === "br") {
            buffer.text += "\n";
            return;
        }
        if (tag === "img") {
            if (mode === "flow") {
                flushText(blocks, "paragraph", buffer.text);
                buffer.text = "";
            }
            blocks.push({ kind: "image", assetUuid: element.getAttribute("data-asset-uuid") ?? undefined });
            return;
        }
        if (tag === "table") {
            if (mode === "flow") {
                flushText(blocks, "paragraph", buffer.text);
                buffer.text = "";
            }
            const rows = parseTableRows(element as HTMLTableElement);
            if (rows.length > 0) {
                blocks.push({ kind: "table", rows });
            }
            return;
        }
        if (tag === "li") {
            const item = { text: "" };
            Array.from(element.childNodes).forEach((child) => walk(child, "list-item", item));
            flushText(blocks, "list-item", item.text);
            return;
        }
        if (/^h[1-6]$/.test(tag)) {
            if (mode === "flow") {
                flushText(blocks, "paragraph", buffer.text);
                buffer.text = "";
            }
            const heading = { text: "" };
            Array.from(element.childNodes).forEach((child) => walk(child, "heading", heading));
            flushText(blocks, "heading", heading.text);
            return;
        }
        if (["p", "div", "blockquote", "pre"].includes(tag) && mode === "flow") {
            flushText(blocks, "paragraph", buffer.text);
            buffer.text = "";
            const nested = { text: "" };
            Array.from(element.childNodes).forEach((child) => walk(child, "flow", nested));
            flushText(blocks, "paragraph", nested.text);
            return;
        }
        Array.from(element.childNodes).forEach((child) => walk(child, mode, buffer));
    };

    const buffer = { text: "" };
    Array.from(root.childNodes).forEach((child) => walk(child, "flow", buffer));
    flushText(blocks, "paragraph", buffer.text);
    return blocks;
};

const resolveCustomerName = async (customerUuid: string | undefined, t: TFunction): Promise<string> => {
    if (!customerUuid) {
        return t("knowledgeBase.form.visibilityGlobal");
    }
    try {
        const response = await new CustomerService().fetchSelectItems();
        const match = (response.data.data ?? []).find((customer) => customer.uuid === customerUuid);
        return match?.name?.trim() || customerUuid;
    } catch {
        return customerUuid;
    }
};

const resolveAccessModuleName = async (accessModuleCode: string | undefined, t: TFunction): Promise<string> => {
    if (!accessModuleCode) {
        return t("knowledgeBase.form.accessModuleNone");
    }
    try {
        const catalog = await new AccessModuleService().getCatalog();
        const match = catalog.find((module) => module.code === accessModuleCode);
        return match ? t(match.nameTag) : accessModuleCode;
    } catch {
        return accessModuleCode;
    }
};

/**
 * Which stills of an asset can go into the PDF, best first (`undefined` = the uploaded file itself). Taken
 * from what the media list says exists, so nothing is asked for that is not there — and a plain attachment
 * (a zip, a PDF) is never downloaded just to find out it is not a picture.
 */
const embeddableStills = (asset: IMediaAssetModel, preferThumbnail: boolean): (string | undefined)[] => {
    const stills: (string | undefined)[] = [];
    if (asset.reducedUrl) {
        stills.push("REDUCED");
    }
    if (asset.thumbnailUrl) {
        stills.push("THUMBNAIL");
    }
    if (preferThumbnail) {
        stills.reverse();
    }
    // A photo without any rendition (SVG): the original is the picture.
    if (stills.length === 0 && asset.mediaType === MEDIA_TYPE.PHOTO) {
        stills.push(undefined);
    }
    return stills;
};

const loadEmbedImage = async (
    mediaAssetService: MediaAssetService,
    asset: IMediaAssetModel,
    cache: Map<string, IPdfEmbedImage | null>,
    preferThumbnail = false
): Promise<IPdfEmbedImage | null> => {
    const cacheKey = `${asset.uuid}:${preferThumbnail ? "thumb" : "full"}`;
    if (cache.has(cacheKey)) {
        return cache.get(cacheKey) ?? null;
    }
    for (const variantType of embeddableStills(asset, preferThumbnail)) {
        try {
            const blob = await mediaAssetService.fetchContent(asset.uuid, variantType);
            if (!blob || blob.size === 0 || blob.type.includes("json")) {
                continue;
            }
            const image = await normalizeImageForPdf(blob);
            cache.set(cacheKey, image);
            return image;
        } catch {
            // Try the next still.
        }
    }
    cache.set(cacheKey, null);
    return null;
};

const drawFittedImage = (
    doc: jsPDF,
    image: IPdfEmbedImage,
    y: number,
    articleTitle: string,
    theme: ReturnType<typeof resolveProjectPdfTheme>,
    maxHeightMm = PDF_IMAGE_MAX_HEIGHT_MM
): number => {
    const { widthMm, heightMm } = fitImageMm(image, PDF_CONTENT_WIDTH, maxHeightMm);
    const currentY = ensurePageSpace(doc, y, heightMm + 2, articleTitle, theme);
    doc.addImage(image.dataUrl, image.format, PDF_MARGIN_X, currentY, widthMm, heightMm);
    return currentY + heightMm + 3;
};

const writeHtmlBlocks = async (
    doc: jsPDF,
    blocks: HtmlBlock[],
    y: number,
    articleTitle: string,
    theme: ReturnType<typeof resolveProjectPdfTheme>,
    mediaByUuid: Record<string, IMediaAssetModel>,
    mediaAssetService: MediaAssetService,
    cache: Map<string, IPdfEmbedImage | null>,
    t: TFunction
): Promise<number> => {
    let currentY = y;
    for (const block of blocks) {
        if (block.kind === "heading") {
            currentY = ensurePageSpace(doc, currentY, PDF_LINE_HEIGHT + 2, articleTitle, theme);
            doc.setFontSize(PDF_FONT_BODY);
            doc.setFont("helvetica", "bold");
            setTextColor(doc, theme.primary);
            const lines = doc.splitTextToSize(block.text, PDF_CONTENT_WIDTH);
            currentY = writeWrappedLines(doc, lines, PDF_MARGIN_X, currentY, PDF_LINE_HEIGHT, articleTitle, theme) + 2;
            continue;
        }
        if (block.kind === "paragraph" || block.kind === "list-item") {
            doc.setFontSize(PDF_FONT_BODY);
            doc.setFont("helvetica", "normal");
            setTextColor(doc, theme.text);
            const prefix = block.kind === "list-item" ? "• " : "";
            const lines = doc.splitTextToSize(`${prefix}${block.text}`, PDF_CONTENT_WIDTH);
            currentY = writeWrappedLines(doc, lines, PDF_MARGIN_X, currentY, PDF_LINE_HEIGHT, articleTitle, theme) + 1;
            continue;
        }
        if (block.kind === "table") {
            for (const row of block.rows) {
                doc.setFontSize(PDF_FONT_SMALL);
                doc.setFont("helvetica", "normal");
                setTextColor(doc, theme.text);
                const line = row.map((cell) => cell || PDF_EMPTY_VALUE).join("  |  ");
                const lines = doc.splitTextToSize(line, PDF_CONTENT_WIDTH);
                currentY = writeWrappedLines(doc, lines, PDF_MARGIN_X, currentY, PDF_LINE_HEIGHT - 0.4, articleTitle, theme);
            }
            currentY += 2;
            continue;
        }
        const asset = block.assetUuid ? mediaByUuid[block.assetUuid] : undefined;
        if (!asset) {
            doc.setFontSize(PDF_FONT_SMALL);
            setTextColor(doc, theme.muted);
            currentY = writeWrappedLines(
                doc,
                [t("knowledgeBase.export.imageUnavailable")],
                PDF_MARGIN_X,
                currentY,
                PDF_LINE_HEIGHT,
                articleTitle,
                theme
            );
            continue;
        }
        const image = await loadEmbedImage(mediaAssetService, asset, cache);
        if (image) {
            currentY = drawFittedImage(doc, image, currentY, articleTitle, theme);
        } else {
            doc.setFontSize(PDF_FONT_SMALL);
            setTextColor(doc, theme.muted);
            currentY = writeWrappedLines(
                doc,
                [t("knowledgeBase.export.imageUnavailable")],
                PDF_MARGIN_X,
                currentY,
                PDF_LINE_HEIGHT,
                articleTitle,
                theme
            );
        }
    }
    return currentY;
};

const writeVideoNote = (
    doc: jsPDF,
    filename: string,
    y: number,
    articleTitle: string,
    theme: ReturnType<typeof resolveProjectPdfTheme>,
    t: TFunction
): number => {
    doc.setFontSize(PDF_FONT_SMALL);
    doc.setFont("helvetica", "italic");
    setTextColor(doc, theme.muted);
    const lines = doc.splitTextToSize(t("knowledgeBase.export.videoNote", { filename }), PDF_CONTENT_WIDTH);
    return writeWrappedLines(doc, lines, PDF_MARGIN_X, y, PDF_LINE_HEIGHT, articleTitle, theme) + 2;
};

export const buildKnowledgeArticlePdf = async ({
    article,
    categories,
    sections,
    mediaAssets,
    mediaAssetService,
    t,
}: IExportKnowledgeArticleToPdfParams): Promise<{ blob: Blob; filename: string }> => {
    const theme = resolveProjectPdfTheme();
    const articleTitle = article.title?.trim() || PDF_EMPTY_VALUE;
    const [customerName, accessModuleName, dashlyLogo, customerLogo] = await Promise.all([
        resolveCustomerName(article.customerUuid, t),
        resolveAccessModuleName(article.accessModuleCode, t),
        resolveDashlyPdfLogo(),
        resolveCustomerPdfLogo(article.customerUuid),
    ]);

    const mediaByUuid = Object.fromEntries(mediaAssets.map((asset) => [asset.uuid, asset]));
    const mediaBySectionUuid: Record<string, IMediaAssetModel[]> = {};
    mediaAssets.forEach((asset) => {
        if (!asset.sectionUuid) {
            return;
        }
        (mediaBySectionUuid[asset.sectionUuid] ??= []).push(asset);
    });
    const imageCache = new Map<string, IPdfEmbedImage | null>();

    const tags = (article.tags ?? "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean)
        .join(", ");
    const readingTime = estimateReadingTimeMinutes(
        sections
            .filter((section) => section.sectionType === SECTION_TYPE.TEXT)
            .map((section) => section.content ?? "")
            .join(" ")
    );
    const featuredLabel = isFeaturedExpired(article)
        ? t("knowledgeBase.featuredExpired")
        : isFeaturedActive(article)
          ? t("knowledgeBase.detail.featuredActive")
          : yesNo(false, t);

    const doc = new jsPDF();
    let y = PDF_MARGIN_TOP;

    const logos = [customerLogo, dashlyLogo].filter(Boolean);
    if (logos.length > 0) {
        let cursorX = PDF_PAGE_WIDTH - PDF_MARGIN_X;
        logos.forEach((logo) => {
            if (!logo) {
                return;
            }
            cursorX -= logo.widthMm;
            doc.addImage(logo.dataUrl, logo.format, cursorX, y - 4, logo.widthMm, logo.heightMm);
            cursorX -= 4;
        });
    }

    doc.setFontSize(PDF_FONT_TITLE);
    doc.setFont("helvetica", "bold");
    setTextColor(doc, theme.primary);
    const titleWidth = logos.length > 0 ? PDF_CONTENT_WIDTH - 50 : PDF_CONTENT_WIDTH;
    const titleLines = doc.splitTextToSize(articleTitle, titleWidth);
    y = writeWrappedLines(doc, titleLines, PDF_MARGIN_X, y + 4, 6.2, articleTitle, theme);

    doc.setFontSize(PDF_FONT_SMALL);
    doc.setFont("helvetica", "normal");
    setTextColor(doc, theme.muted);
    y = writeWrappedLines(
        doc,
        [t("knowledgeBase.export.exportedOn", { date: formatPdfDate(new Date().toISOString()) })],
        PDF_MARGIN_X,
        y + 1,
        PDF_LINE_HEIGHT,
        articleTitle,
        theme
    );
    y += 4;

    y = drawDashboardTitle(doc, t("knowledgeBase.form.settings"), y, articleTitle, theme);
    y = writeFieldGrid(
        doc,
        [
            { label: t("knowledgeBase.form.status"), value: t(`knowledgeBase.status.${(article.status ?? "").toLowerCase()}`) },
            { label: t("knowledgeBase.form.visibilityCustomer"), value: customerName },
            { label: t("knowledgeBase.form.validFrom"), value: formatPdfDate(article.validFrom) },
            { label: t("knowledgeBase.form.expiresAt"), value: formatPdfDate(article.expiresAt) },
            { label: t("knowledgeBase.form.featured"), value: featuredLabel },
            { label: t("knowledgeBase.form.featuredExpiresAt"), value: formatPdfDate(article.featuredExpiresAt) },
            { label: t("knowledgeBase.form.accessModuleCode"), value: accessModuleName },
            { label: t("knowledgeBase.statViews"), value: String(article.viewCount ?? 0) },
            { label: t("knowledgeBase.statReadingTime"), value: String(readingTime) },
            { label: t("knowledgeBase.form.categories"), value: categories.map((category) => category.categoryName).join(", ") },
            { label: t("knowledgeBase.form.tags"), value: tags },
        ],
        y,
        articleTitle,
        theme
    );
    y += PDF_SECTION_GAP;

    y = drawDashboardTitle(doc, t("knowledgeBase.form.sections"), y, articleTitle, theme);
    if (sections.length === 0) {
        doc.setFontSize(PDF_FONT_BODY);
        setTextColor(doc, theme.muted);
        y = writeWrappedLines(doc, [t("knowledgeBase.export.noSections")], PDF_MARGIN_X, y, PDF_LINE_HEIGHT, articleTitle, theme);
    }
    for (const section of sections) {
        const heading = section.showTitle !== false && section.title
            ? `${section.title} (${sectionTypeLabel(section.sectionType, t)})`
            : sectionTypeLabel(section.sectionType, t);
        y = ensurePageSpace(doc, y, PDF_LINE_HEIGHT + 4, articleTitle, theme);
        doc.setFontSize(PDF_FONT_LABEL);
        doc.setFont("helvetica", "bold");
        setTextColor(doc, theme.primary);
        const headingLines = doc.splitTextToSize(heading.toUpperCase(), PDF_CONTENT_WIDTH);
        y = writeWrappedLines(doc, headingLines, PDF_MARGIN_X, y, PDF_LINE_HEIGHT, articleTitle, theme) + 2;

        const sectionAssets = mediaBySectionUuid[section.uuid] ?? [];
        if (section.sectionType === SECTION_TYPE.TEXT) {
            y = await writeHtmlBlocks(
                doc,
                parseHtmlBlocks(section.content ?? ""),
                y,
                articleTitle,
                theme,
                mediaByUuid,
                mediaAssetService,
                imageCache,
                t
            );
        } else if (section.sectionType === SECTION_TYPE.VIDEO) {
            const asset = sectionAssets[0];
            if (asset) {
                const poster = await loadEmbedImage(mediaAssetService, asset, imageCache, true);
                if (poster) {
                    y = drawFittedImage(doc, poster, y, articleTitle, theme);
                }
                y = writeVideoNote(doc, asset.originalFilename, y, articleTitle, theme, t);
            }
        } else {
            for (const asset of sectionAssets) {
                if (asset.mediaType === MEDIA_TYPE.VIDEO || isVideoAttachment(asset)) {
                    const poster = await loadEmbedImage(mediaAssetService, asset, imageCache, true);
                    if (poster) {
                        y = drawFittedImage(doc, poster, y, articleTitle, theme);
                    }
                    y = writeVideoNote(doc, asset.originalFilename, y, articleTitle, theme, t);
                } else {
                    const image = await loadEmbedImage(mediaAssetService, asset, imageCache);
                    if (image) {
                        y = drawFittedImage(doc, image, y, articleTitle, theme);
                    } else {
                        doc.setFontSize(PDF_FONT_SMALL);
                        setTextColor(doc, theme.muted);
                        y = writeWrappedLines(
                            doc,
                            [asset.originalFilename || t("knowledgeBase.export.imageUnavailable")],
                            PDF_MARGIN_X,
                            y,
                            PDF_LINE_HEIGHT,
                            articleTitle,
                            theme
                        );
                    }
                }
            }
        }
        y += 4;
    }

    y += 2;
    y = drawDashboardTitle(doc, t("knowledgeBase.attachments"), y, articleTitle, theme);
    if (mediaAssets.length === 0) {
        doc.setFontSize(PDF_FONT_BODY);
        setTextColor(doc, theme.muted);
        y = writeWrappedLines(doc, [t("knowledgeBase.export.noAttachments")], PDF_MARGIN_X, y, PDF_LINE_HEIGHT, articleTitle, theme);
    }
    for (const asset of mediaAssets) {
        y = ensurePageSpace(doc, y, PDF_THUMB_HEIGHT_MM + 4, articleTitle, theme);
        const thumb = await loadEmbedImage(mediaAssetService, asset, imageCache, true);
        let textX = PDF_MARGIN_X;
        if (thumb) {
            const { widthMm, heightMm } = fitImageMm(thumb, 28, PDF_THUMB_HEIGHT_MM);
            doc.addImage(thumb.dataUrl, thumb.format, PDF_MARGIN_X, y, widthMm, heightMm);
            textX = PDF_MARGIN_X + widthMm + 4;
        }
        doc.setFontSize(PDF_FONT_BODY);
        doc.setFont("helvetica", "bold");
        setTextColor(doc, theme.text);
        const nameLines = doc.splitTextToSize(asset.originalFilename || PDF_EMPTY_VALUE, PDF_PAGE_WIDTH - PDF_MARGIN_X - textX);
        let textY = writeWrappedLines(doc, nameLines, textX, y + 4, PDF_LINE_HEIGHT, articleTitle, theme);
        doc.setFont("helvetica", "normal");
        setTextColor(doc, theme.muted);
        textY = writeWrappedLines(
            doc,
            [`${attachmentTypeLabel(asset, t)}  ·  ${formatFileSize(asset.sizeBytes)}`],
            textX,
            textY,
            PDF_LINE_HEIGHT - 0.4,
            articleTitle,
            theme
        );
        y = Math.max(y + (thumb ? PDF_THUMB_HEIGHT_MM : 0), textY) + 4;
    }

    return buildFinalizedPdfBlob(doc, articleTitle, t("knowledgeBase.export.filenameSuffix"), theme);
};

export const exportKnowledgeArticleToPdf = async (params: IExportKnowledgeArticleToPdfParams): Promise<void> => {
    const { blob, filename } = await buildKnowledgeArticlePdf(params);
    saveAs(blob, filename);
};
